import * as THREE from 'three';
import { TerrainFeature } from './TerrainFeature';
import { HillConfig, TerrainBounds, TERRAIN_COLORS } from './TerrainConfig';

/**
 * Hill - Gentle rolling hills for scenic terrain.
 * Height range: 2-6m (background elements, not for tier access).
 * Uses Gaussian falloff for smooth, natural appearance.
 */
export class Hill extends TerrainFeature {
  private height: number;
  private radius: number;

  constructor(scene: THREE.Scene, config: HillConfig) {
    super(scene, config.position, config.rotation || 0, 'hill');

    this.height = config.height;
    this.radius = config.radius;

    this.createMesh();
    this.addToScene();
  }

  private createMesh(): void {
    // Create smooth dome geometry
    const segments = 16;
    const geometry = new THREE.BufferGeometry();
    const vertices: number[] = [];
    const indices: number[] = [];

    // Center vertex at peak
    vertices.push(0, this.height, 0);

    // Generate rings from peak to base
    for (let ring = 1; ring <= segments; ring++) {
      const ringProgress = ring / segments;
      const ringRadius = this.radius * ringProgress;
      const ringHeight = this.height * this.gaussianFalloff(ringRadius, this.radius);

      const ringVertices = segments;
      for (let v = 0; v < ringVertices; v++) {
        const theta = (v / ringVertices) * Math.PI * 2;
        vertices.push(
          Math.cos(theta) * ringRadius,
          ringHeight,
          Math.sin(theta) * ringRadius
        );
      }
    }

    // Generate indices - center fan
    for (let v = 0; v < segments; v++) {
      indices.push(0, v + 1, ((v + 1) % segments) + 1);
    }

    // Generate indices - ring strips
    for (let ring = 1; ring < segments; ring++) {
      const innerStart = 1 + (ring - 1) * segments;
      const outerStart = 1 + ring * segments;

      for (let v = 0; v < segments; v++) {
        const inner1 = innerStart + v;
        const inner2 = innerStart + ((v + 1) % segments);
        const outer1 = outerStart + v;
        const outer2 = outerStart + ((v + 1) % segments);

        indices.push(inner1, outer1, inner2);
        indices.push(inner2, outer1, outer2);
      }
    }

    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(vertices, 3)
    );
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    // Randomized color variation for variety
    const colorVariation = 0.1 + Math.random() * 0.1;
    const hillColor = new THREE.Color(TERRAIN_COLORS.BASE).lerp(
      new THREE.Color(TERRAIN_COLORS.BASE_LIGHT),
      colorVariation
    );

    const material = this.createTerrainMaterial(hillColor.getHex());
    const hillMesh = new THREE.Mesh(geometry, material);
    this.mesh.add(hillMesh);
  }

  getHeightAtPosition(worldPos: THREE.Vector3): number | null {
    const local = this.worldToLocal(worldPos);
    const distance = Math.sqrt(local.x * local.x + local.z * local.z);

    // Check if within radius
    if (distance > this.radius * 1.2) {
      return null;
    }

    const height = this.height * this.gaussianFalloff(distance, this.radius);

    if (height < 0.1) {
      return null;
    }

    return height;
  }

  getBounds(): TerrainBounds {
    const extent = this.radius * 1.2;
    return {
      minX: this.position.x - extent,
      maxX: this.position.x + extent,
      minZ: this.position.z - extent,
      maxZ: this.position.z + extent,
    };
  }
}
